import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import UserFormModal from '../components/UserFormModal';
import InvitationFormModal from '../components/InvitationFormModal';
import PasswordResetModal from '../components/PasswordResetModal';
import './ManageUsers.css';

function ManageUsers() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const { data: users, loading, error, executeRequest } = useApiState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadUsers();
    loadInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Filter users based on search term and role restrictions
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.username.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          (u.display_name && u.display_name.toLowerCase().includes(term))
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, isSuperAdmin]);

  const loadUsers = async () => {
    try {
      await executeRequest(async () => {
        const response = await adminAPI.getAllUsers();
        return response.data.users || [];
      });
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadInvitations = async () => {
    try {
      const response = await adminAPI.getInvitations();
      setInvitations(response.data.invitations || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleSaveUser = async (formData) => {
    try {
      if (modalMode === 'create') {
        await adminAPI.createUser({
          username: formData.username,
          email: formData.email,
          displayName: formData.displayName,
          role: formData.role,
          isActive: formData.isActive,
        });
        showToast('User created successfully!');
      } else {
        await adminAPI.updateUser(selectedUser.id, {
          email: formData.email,
          displayName: formData.displayName,
          role: formData.role,
          isActive: formData.isActive,
          notifyRoundReminders: formData.notifyRoundReminders,
          notifyMatchReminders: formData.notifyMatchReminders,
        });
        showToast('User updated successfully!');
      }
      await loadUsers();
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleInviteUser = () => {
    setShowInvitationModal(true);
  };

  const handleSendInvitation = async (formData) => {
    try {
      await adminAPI.sendInvitation(formData.email, formData.role);
      showToast(`Invitation sent to ${formData.email}`);
      await loadInvitations();
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleResendInvitation = async (invitation) => {
    if (!window.confirm(`Resend invitation to ${invitation.email}?`)) {
      return;
    }

    try {
      await adminAPI.resendInvitation(invitation.id);
      showToast(`Invitation resent to ${invitation.email}`);
      await loadInvitations();
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      showToast(err.response?.data?.error || 'Failed to resend invitation', 'error');
    }
  };

  const handleRevokeInvitation = async (invitation) => {
    if (!window.confirm(`Revoke invitation for ${invitation.email}?`)) {
      return;
    }

    try {
      await adminAPI.revokeInvitation(invitation.id);
      showToast(`Invitation for ${invitation.email} has been revoked`);
      await loadInvitations();
    } catch (err) {
      console.error('Failed to revoke invitation:', err);
      showToast(err.response?.data?.error || 'Failed to revoke invitation', 'error');
    }
  };

  const handlePasswordReset = (user) => {
    setResetTargetUser(user);
    setShowResetModal(true);
  };

  const handleResendVerification = async (user) => {
    if (!window.confirm(`Resend verification email to ${user.email}?`)) {
      return;
    }

    try {
      await adminAPI.resendVerification(user.id);
      showToast(`Verification email sent to ${user.email}`);
    } catch (err) {
      console.error('Failed to send verification email:', err);
      showToast(err.response?.data?.error || 'Failed to send verification email', 'error');
    }
  };

  const handleUnlockAccount = async (user) => {
    const locked = isAccountLocked(user);
    const confirmMsg = locked
      ? `Unlock account for ${user.username}? This will remove the lockout and reset failed login attempts.`
      : `Reset failed login attempts for ${user.username}?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await adminAPI.unlockAccount(user.id);
      showToast(locked ? `Account unlocked for ${user.username}` : `Failed login attempts reset for ${user.username}`);
      await loadUsers(); // Refresh the user list
    } catch (err) {
      console.error('Failed to unlock account:', err);
      showToast(err.response?.data?.error || 'Failed to unlock account', 'error');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin':
        return 'role-badge super-admin';
      case 'admin':
        return 'role-badge admin';
      default:
        return 'role-badge user';
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      default:
        return 'User';
    }
  };

  const canModifyUser = (user) => {
    // Super admin can modify everyone
    if (isSuperAdmin) return true;
    // Regular admin can only modify regular users
    return user.role === 'user';
  };

  const isAccountLocked = (user) => {
    return user.locked_until && new Date(user.locked_until) > new Date();
  };

  const hasFailedAttempts = (user) => {
    return user.failed_login_attempts > 0;
  };

  const getLockoutStatus = (user) => {
    if (isAccountLocked(user)) {
      const lockedUntil = new Date(user.locked_until);
      const minutesRemaining = Math.ceil((lockedUntil - new Date()) / (1000 * 60));
      return {
        status: 'locked',
        emoji: 'Locked',
        title: `Account locked - ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} remaining`,
        //badgeClass: 'lockout-badge locked',
        badgeClass: 'status-badge locked',
      };
    } else if (hasFailedAttempts(user)) {
      return {
        status: 'warning',
        emoji: 'Warning',
        title: `${user.failed_login_attempts} failed login attempt${user.failed_login_attempts !== 1 ? 's' : ''}`,
        badgeClass: 'status-badge warning',
      };
    }
    return {
      status: 'ok',
      emoji: 'Unlocked',
      title: 'No lockout issues',
      badgeClass: 'status-badge active',
    };
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadUsers} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="manage-users-container">
      <div className="page-header">
        <button onClick={() => navigate('/picks')} className="btn-back">
          ← Back to Picks
        </button>
        <h1>Manage Users</h1>
        <div className="header-actions">
          <button onClick={handleInviteUser} className="btn btn-secondary">
            Invite User
          </button>
          <button onClick={handleCreateUser} className="btn btn-primary">
            + Create User
          </button>
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by username, email, or display name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="clear-search">
            Clear
          </button>
        )}
      </div>

      {invitations.length > 0 && (
        <div className="invitations-section">
          <h2>Pending Invitations</h2>
          <div className="invitations-table-container">
            <table className="invitations-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited By</th>
                  <th>Expires At</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td className="email-cell">{invitation.email}</td>
                    <td>
                      <span className={getRoleBadgeClass(invitation.role)}>
                        {getRoleDisplay(invitation.role)}
                      </span>
                    </td>
                    <td>{invitation.invitedBy}</td>
                    <td>{new Date(invitation.expiresAt).toLocaleString()}</td>
                    <td>
                      <span className={`status-badge ${invitation.isExpired ? 'inactive' : 'warning'}`}>
                        {invitation.isExpired ? 'Expired' : 'Pending'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleResendInvitation(invitation)}
                        className="btn-action btn-reset"
                        title="Resend invitation"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleRevokeInvitation(invitation)}
                        className="btn-action btn-delete"
                        title="Revoke invitation"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Display Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Verified</th>
              <th>Lockout</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-results">
                  {searchTerm ? 'No users found matching your search' : 'No users available'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="username-cell">{user.username}</td>
                  <td>{user.display_name || '-'}</td>
                  <td className="email-cell">{user.email}</td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>
                      {getRoleDisplay(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${user.is_email_verified ? 'active' : 'unverified'}`}
                    >
                      {user.is_email_verified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={getLockoutStatus(user).badgeClass}
                      title={getLockoutStatus(user).title}
                    >
                      {getLockoutStatus(user).emoji}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handlePasswordReset(user)}
                      className="btn-action btn-reset"
                      title="Send password reset"
                    >
                      Reset
                    </button>
                    {canModifyUser(user) && (
                      <>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="btn-action btn-edit"
                          title="Edit user"
                        >
                          Edit
                        </button>
                        {!user.is_email_verified && (
                          <button
                            onClick={() => handleResendVerification(user)}
                            className="btn-action btn-verify"
                            title="Resend verification email"
                          >
                            Resend
                          </button>
                        )}
                        {(isAccountLocked(user) || hasFailedAttempts(user)) && (
                          <button
                            onClick={() => handleUnlockAccount(user)}
                            className="btn-action btn-unlock"
                            title={isAccountLocked(user) ? 'Unlock account and reset failed attempts' : 'Reset failed login attempts'}
                          >
                            {isAccountLocked(user) ? 'Unlock' : 'Reset Attempts'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="users-summary">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      {showModal && (
        <UserFormModal
          mode={modalMode}
          user={selectedUser}
          onClose={() => setShowModal(false)}
          onSave={handleSaveUser}
        />
      )}

      {showInvitationModal && (
        <InvitationFormModal
          onClose={() => setShowInvitationModal(false)}
          onSend={handleSendInvitation}
        />
      )}

      {showResetModal && resetTargetUser && (
        <PasswordResetModal
          user={resetTargetUser}
          onClose={() => { setShowResetModal(false); setResetTargetUser(null); }}
          onSuccess={(message) => { showToast(message); loadUsers(); }}
        />
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

export default ManageUsers;
